import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import ImageUploader from '../tools/uploaderImage';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';
export default function TissImage({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
  const { t } = useTranslation();
  const cookie = getCookies();

  // Helper to get multilingual value
  const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
    if (!field) return fallback;
    if (typeof field === 'string') return field; // Legacy support
    if (typeof field === 'object') {
      return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
    }
    return fallback;
  };

  // Helper to set multilingual value
  const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
    if (!currentData || typeof currentData !== 'object') {
      return { [lang]: value };
    }
    return {
      ...currentData,
      [lang]: value
    };
  };

  // Current image & alt from props
  const currentImageUrl = data?.toimage?.url ? `${config.fronturl}${data.toimage.url}` : null;
  
  // Get current alt text for the active language
  const getCurrentAltForLang = () => {
    const altField = data?.data?.alt || {};
    return getMultilingualValue(altField, activeLang);
  };

  // Initialize multilingual data from props
  const initializeMultilingualData = () => {
    const altField = data?.data?.alt || {};
    if (typeof altField === 'object' && altField !== null && !Array.isArray(altField)) {
      return { ...altField };
    } else if (typeof altField === 'string' && altField) {
      return { en: altField };
    }
    return {};
  };

  // State - Store ALL multilingual data in state (all 5 languages at once)
  const [multilingualAlt, setMultilingualAlt] = React.useState<any>(initializeMultilingualData());
  
  // Get current language alt from multilingual state
  const currentAlt = multilingualAlt[activeLang] || '';
  const hasContent = !!currentImageUrl;
  
  const [isEditing, setIsEditing] = React.useState(!hasContent);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const prevLangRef = React.useRef(activeLang);
  const prevDataIdRef = React.useRef(data?._id);

  // Update multilingual state when data from server changes
  React.useEffect(() => {
    if (prevDataIdRef.current !== data?._id) {
      const altField = data?.data?.alt || {};
      
      // Initialize multilingual data from server
      let newMultilingualData: any;
      if (typeof altField === 'object' && altField !== null && !Array.isArray(altField)) {
        // Merge with existing state to preserve languages
        newMultilingualData = { ...multilingualAlt, ...altField };
      } else if (typeof altField === 'string' && altField) {
        // Legacy format - preserve existing languages, add this as 'en'
        newMultilingualData = { ...multilingualAlt, en: altField };
      } else {
        // Empty - keep existing multilingual data
        newMultilingualData = { ...multilingualAlt };
      }
      
      setMultilingualAlt(newMultilingualData);
      setHasChanges(false);
      prevDataIdRef.current = data?._id;
    }
    
    // Update ref when language changes
    if (prevLangRef.current !== activeLang) {
      setHasChanges(false);
      prevLangRef.current = activeLang;
    }
  }, [data?._id, data?.data?.alt, activeLang]);

  // Track changes - compare with multilingual state
  React.useEffect(() => {
    const originalAlt = data?.data?.alt || {};
    const originalForLang = typeof originalAlt === 'object' && originalAlt !== null 
      ? (originalAlt[activeLang] || '')
      : (activeLang === 'en' ? originalAlt : '');
    setHasChanges(currentAlt !== originalForLang);
  }, [currentAlt, activeLang, data?.data?.alt]);

  // Handle Escape key to close preview
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPreview) {
        setShowPreview(false);
      }
    };
    if (showPreview) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showPreview]);

  // Save alt text (image is saved by ImageUploader) - save ALL languages at once
  const saveAltText = async () => {
    if (!data?._id) return;

    setSaving(true);
    try {
      console.log('=== SAVING ALL MULTILINGUAL ALT ===');
      console.log('All languages in state:', multilingualAlt);
      
      // Send ALL multilingual data to backend
      const dataToSend = {
        alt: multilingualAlt // Send complete multilingual object
      };
      
      console.log('Sending to backend:', JSON.stringify(dataToSend, null, 2));
      
      await axios.put(
        `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
        dataToSend,
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );
      
      Swal.fire({
        title: t('ذخیره شد'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
      });
      setIsEditing(false);
      setHasChanges(false);
      reload();
    } catch (error) {
      console.error('Save failed:', error);
      Swal.fire({
        title: t('Error saving'),
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel handler with confirmation if there are changes
  const handleCancel = () => {
    if (hasChanges) {
      Swal.fire({
        title: t('Discard changes?'),
        text: t('You have unsaved changes. Are you sure you want to cancel?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('Yes, discard'),
        cancelButtonText: t('No, keep editing'),
      }).then((result) => {
        if (result.isConfirmed) {
          // Reload from server to reset all changes
          reload();
          setIsEditing(false);
          setHasChanges(false);
        }
      });
    } else {
      setIsEditing(false);
    }
  };

  // Placeholder / Preview
  if (!isEditing) {
    return (
      <>
        <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
          <div className="p-0.5 relative">
            {currentImageUrl ? (
              <div className="relative rounded-lg overflow-hidden bg-gray-50">
                <div className="relative w-full">
                  <img
                    src={currentImageUrl}
                    alt={currentAlt || t('تصویر')}
                    className="max-w-full h-auto w-full object-contain block cursor-pointer"
                    onClick={() => setShowPreview(true)}
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder-image.jpg';
                      e.currentTarget.className = 'max-w-full h-auto w-full object-contain opacity-50';
                    }}
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 pointer-events-none"></div>
                </div>
                {currentAlt && (
                  <div className="mt-2 text-sm text-gray-600 text-right px-1">
                    <span className="text-gray-500 text-xs">{t('توضیح تصویر')}:</span>
                    <span className="ml-1">{currentAlt}</span>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-12 cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
                onClick={() => setIsEditing(true)}
              >
                <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
                  {t('کلیک برای افزودن تصویر')}
                </span>
                <span className="text-gray-400 text-xs mt-1">
                  {t('Upload an image file')}
                </span>
              </div>
            )}

            {/* Action Buttons - Appear on hover */}
            {currentImageUrl && (
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 flex gap-2">
                <button
                  onClick={() => setShowPreview(true)}
                  className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {t('پیش‌نمایش')}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('ویرایش تصویر')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && currentImageUrl && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
              <button
                onClick={() => setShowPreview(false)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 z-10"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={currentImageUrl}
                alt={currentAlt || t('پیش‌نمایش تصویر')}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder-image.jpg';
                  e.currentTarget.className = 'max-w-full max-h-full object-contain opacity-50 rounded-lg';
                }}
              />
              {currentAlt && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm max-w-2xl text-center">
                  {currentAlt}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // Edit Mode
  return (
    <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
      <div className="p-0.5">
        <div className="space-y-5">
          {/* Image Uploader */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
              {t('آپلود تصویر')}
            </label>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:border-gray-300 transition-colors duration-200">
              <ImageUploader id={data._id} component={'ImageEditor'} />
            </div>
            {currentImageUrl && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-3 text-right font-medium">{t('پیش‌نمایش')}:</p>
                <div className="overflow-hidden rounded-lg bg-white border border-gray-200">
                  <img
                    src={currentImageUrl}
                    alt={currentAlt || t('پیش‌نمایش')}
                    className="max-w-full h-auto w-full object-contain block"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Alt Text */}
          <div>
            <label htmlFor="alt-text" className="block text-sm font-medium text-gray-700 mb-2 text-right">
              {t('متن جایگزین (Alt)')}
            </label>
            <Input
              key={`alt-input-${activeLang}`}
              id="alt-text"
              value={currentAlt}
              onChange={(value) => {
                const newAlt = value || '';
                // Update only the current language, preserve all others
                setMultilingualAlt((prev: any) => ({
                  ...prev,
                  [activeLang]: newAlt
                }));
              }}
              placeholder={t('توضیح تصویر برای دسترسی‌پذیری...')}
              className="w-full"
              style={{
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                textAlign: 'right',
              }}
            />
            <p className="mt-2 text-xs text-gray-500 text-right">
              {t('این متن برای دسترسی‌پذیری و SEO مهم است')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3 justify-end items-center">
          {hasChanges && (
            <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
              {t('Unsaved changes')}
            </span>
          )}
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {t('لغو')}
          </button>
          <button
            type="button"
            onClick={saveAltText}
            disabled={saving || !hasChanges}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('ذخیره...')}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('ذخیره')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}